import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const GetStartedSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [pointerEventsEnabled, setPointerEventsEnabled] = useState(true);

  // Cards data with content and visuals
  const cards = [
    { 
      id: 1, 
      title: "Discovery Call",
      description: "We understand your goals and identify automation opportunities through a quick strategy session.",
      color: "from-blue-500 to-cyan-500"
    },
    { 
      id: 2, 
      title: "Build & Production",
      description: "Our team designs, develops, and tests your custom AI solution with precision.",
      color: "from-purple-500 to-pink-500"
    },
    { 
      id: 3, 
      title: "Implementation",
      description: "We deploy your system seamlessly and ensure it runs flawlessly in your workflow.",
      color: "from-green-500 to-emerald-500"
    }
  ];

  // Intersection Observer to detect when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Scroll progress calculation
  const scrollProgress = useTransform(scrollYProgress, [0, 1], [0, 3]);
  const pointerEventsThreshold = Math.max(cards.length - 0.35, 0);
  
  useEffect(() => {
    const unsubscribe = scrollProgress.onChange((latest) => {
      const newActiveIndex = Math.min(Math.floor(latest), cards.length - 1);
      setActiveCardIndex(newActiveIndex);
      setPointerEventsEnabled(latest < pointerEventsThreshold);
    });

    return unsubscribe;
  }, [scrollProgress, cards.length, pointerEventsThreshold]);

  const getCardTransform = (index) => {
    if (!isIntersecting) {
      return {
        transform: `translateY(100px) scale(0.8)`,
        opacity: 0,
        zIndex: 10 + index,
        pointerEvents: "none",
      };
    }

    const distanceFromActive = activeCardIndex - index;
    const isCardVisible = index <= activeCardIndex;
    
    if (!isCardVisible) {
      return {
        transform: `translateY(100px) scale(0.8)`,
        opacity: 0,
        zIndex: 10 + index,
        pointerEvents: "none",
      };
    }

    const scale = index === activeCardIndex ? 1 : Math.max(0.8, 1 - (activeCardIndex - index) * 0.1);
    const translateY = `${(activeCardIndex - index) * 20}px`;
    const opacity = index === activeCardIndex ? 1 : 1;

    return {
      transform: `translateY(${translateY}) scale(${scale})`,
      opacity: opacity,
      zIndex: 10 + index,
      pointerEvents: isCardVisible ? "auto" : "none",
    };
  };

  return (
    <section ref={containerRef} className="relative min-h-[400vh] bg-white">
      
      {/* Sticky container for the cards */}
      <div
        className="sticky top-0 h-screen flex items-center justify-center overflow-hidden"
        style={{ pointerEvents: pointerEventsEnabled ? "auto" : "none" }}
      >
        <div className="text-center mb-16 ml-16 lg:ml-32 mt-40">
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 font-slim">
            Ready To Get Started?
          </h2>
          
          {/* Motion Graphic - Adapted for White Background */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              transition: {
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1]
              }
            }}
            className="relative mb-8"
          >
            <svg
              width="200"
              height="200"
              viewBox="0 0 100 100"
              className="mx-auto"
              style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
            >
              {/* Main Nodes - Dark colors for white background */}
              <motion.circle
                cx="20" cy="20" r="3"
                fill="#1f2937"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.circle
                cx="80" cy="20" r="3"
                fill="#374151"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
              <motion.circle
                cx="20" cy="80" r="3"
                fill="#4b5563"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.6
                }}
              />
              <motion.circle
                cx="80" cy="80" r="3"
                fill="#6b7280"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.9
                }}
              />
              <motion.circle
                cx="50" cy="50" r="4"
                fill="#111827"
                animate={{ 
                  scale: [0.9, 1.3, 0.9],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ 
                  duration: 2.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.4
                }}
              />
              
              {/* Neural Network Connections - Dark colors */}
              <motion.line
                x1="20" y1="20" x2="80" y2="20"
                stroke="#374151" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.line
                x1="20" y1="80" x2="80" y2="80"
                stroke="#4b5563" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />
              <motion.line
                x1="20" y1="20" x2="20" y2="80"
                stroke="#6b7280" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
              <motion.line
                x1="80" y1="20" x2="80" y2="80"
                stroke="#9ca3af" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              />
              
              {/* Diagonal Connections */}
              <motion.line
                x1="20" y1="20" x2="80" y2="80"
                stroke="#374151" strokeWidth="0.3" opacity="0.3"
                animate={{ opacity: [0.1, 0.6, 0.1] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
              />
              <motion.line
                x1="80" y1="20" x2="20" y2="80"
                stroke="#4b5563" strokeWidth="0.3" opacity="0.3"
                animate={{ opacity: [0.1, 0.6, 0.1] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
              />
              
              {/* Center connections */}
              <motion.line
                x1="20" y1="20" x2="50" y2="50"
                stroke="#6b7280" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 2.1, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
              />
              <motion.line
                x1="80" y1="20" x2="50" y2="50"
                stroke="#9ca3af" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
              />
              <motion.line
                x1="20" y1="80" x2="50" y2="50"
                stroke="#374151" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
              />
              <motion.line
                x1="80" y1="80" x2="50" y2="50"
                stroke="#4b5563" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
              />
              
              {/* Additional Layer Nodes - Colorful but subtle */}
              <motion.circle
                cx="30" cy="30" r="2"
                fill="#3b82f6"
                animate={{ 
                  scale: [0.6, 1.4, 0.6],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{ 
                  duration: 2.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.2
                }}
              />
              <motion.circle
                cx="70" cy="30" r="2"
                fill="#10b981"
                animate={{ 
                  scale: [0.6, 1.4, 0.6],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{ 
                  duration: 2.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.8
                }}
              />
              <motion.circle
                cx="30" cy="70" r="2"
                fill="#f59e0b"
                animate={{ 
                  scale: [0.6, 1.4, 0.6],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{ 
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.2
                }}
              />
              <motion.circle
                cx="70" cy="70" r="2"
                fill="#ef4444"
                animate={{ 
                  scale: [0.6, 1.4, 0.6],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{ 
                  duration: 2.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.6
                }}
              />
              
              {/* Secondary Layer Connections */}
              <motion.line
                x1="30" y1="30" x2="70" y2="30"
                stroke="#3b82f6" strokeWidth="0.3" opacity="0.4"
                animate={{ opacity: [0.1, 0.6, 0.1] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
              />
              <motion.line
                x1="30" y1="70" x2="70" y2="70"
                stroke="#10b981" strokeWidth="0.3" opacity="0.4"
                animate={{ opacity: [0.1, 0.6, 0.1] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
              />
              <motion.line
                x1="30" y1="30" x2="30" y2="70"
                stroke="#f59e0b" strokeWidth="0.3" opacity="0.4"
                animate={{ opacity: [0.1, 0.6, 0.1] }}
                transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
              />
              <motion.line
                x1="70" y1="30" x2="70" y2="70"
                stroke="#ef4444" strokeWidth="0.3" opacity="0.4"
                animate={{ opacity: [0.1, 0.6, 0.1] }}
                transition={{ duration: 2.3, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
              />
              
              {/* Cross-connections to center */}
              <motion.line
                x1="30" y1="30" x2="50" y2="50"
                stroke="#8b5cf6" strokeWidth="0.2" opacity="0.3"
                animate={{ opacity: [0.05, 0.5, 0.05] }}
                transition={{ duration: 2.7, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
              />
              <motion.line
                x1="70" y1="30" x2="50" y2="50"
                stroke="#06b6d4" strokeWidth="0.2" opacity="0.3"
                animate={{ opacity: [0.05, 0.5, 0.05] }}
                transition={{ duration: 2.1, repeat: Infinity, ease: "easeInOut", delay: 1.0 }}
              />
              <motion.line
                x1="30" y1="70" x2="50" y2="50"
                stroke="#a855f7" strokeWidth="0.2" opacity="0.3"
                animate={{ opacity: [0.05, 0.5, 0.05] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              />
              <motion.line
                x1="70" y1="70" x2="50" y2="50"
                stroke="#22c55e" strokeWidth="0.2" opacity="0.3"
                animate={{ opacity: [0.05, 0.5, 0.05] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />
              
              {/* Particle Effects - Colorful but subtle */}
              <motion.circle
                cx="10"
                cy="10"
                r="1"
                fill="#3b82f6"
                animate={{ 
                  scale: [0.5, 1.5, 0.5],
                  opacity: [0.3, 0.8, 0.3]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.circle
                cx="90"
                cy="15"
                r="0.8"
                fill="#10b981"
                animate={{ 
                  scale: [0.3, 1.2, 0.3],
                  opacity: [0.2, 0.7, 0.2]
                }}
                transition={{ 
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />
              <motion.circle
                cx="85"
                cy="85"
                r="1.2"
                fill="#f59e0b"
                animate={{ 
                  scale: [0.4, 1.8, 0.4],
                  opacity: [0.3, 0.9, 0.3]
                }}
                transition={{ 
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />
              <motion.circle
                cx="15"
                cy="90"
                r="0.6"
                fill="#ef4444"
                animate={{ 
                  scale: [0.2, 1.3, 0.2],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{ 
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.5
                }}
              />
              
              {/* Additional floating particles */}
              <motion.circle
                cx="5" cy="50" r="0.5"
                fill="#8b5cf6"
                animate={{ 
                  scale: [0.3, 1.0, 0.3],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
              <motion.circle
                cx="95" cy="50" r="0.5"
                fill="#06b6d4"
                animate={{ 
                  scale: [0.3, 1.0, 0.3],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{ 
                  duration: 1.7,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.1
                }}
              />
              <motion.circle
                cx="50" cy="5" r="0.5"
                fill="#a855f7"
                animate={{ 
                  scale: [0.3, 1.0, 0.3],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{ 
                  duration: 1.9,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.7
                }}
              />
              <motion.circle
                cx="50" cy="95" r="0.5"
                fill="#22c55e"
                animate={{ 
                  scale: [0.3, 1.0, 0.3],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{ 
                  duration: 2.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.3
                }}
              />
            </svg>
          </motion.div>
        </div>
        
        {/* Cards container */}
        <div className="relative w-full max-w-6xl mx-auto flex items-center justify-center">
          {cards.map((card, index) => {
            const cardTransform = getCardTransform(index);
            
            return (
              <div
                key={card.id}
                className="absolute w-[500px] h-80 rounded-2xl shadow-2xl border-2 border-gray-300 transition-all duration-500 ease-out overflow-hidden"
                style={{
                  ...cardTransform,
                  top: "50%",
                  left: "50%",
                  transform: `translateX(-50%) translateY(-50%) ${cardTransform.transform}`,
                  backgroundColor: 'oklch(0.23 0.002 264)'
                }}
              >
                {/* Gradient background overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-5`}></div>
                
                {/* Animated background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    <defs>
                      <pattern id={`pattern-${card.id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="10" cy="10" r="1" fill="currentColor" opacity="0.3"/>
                        <line x1="0" y1="10" x2="20" y2="10" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
                        <line x1="10" y1="0" x2="10" y2="20" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill={`url(#pattern-${card.id})`}/>
                  </svg>
                </div>

                <div className="relative p-8 h-full flex flex-col items-center justify-center">
                  {/* Motion Graphic Icon */}
                  <div className="w-20 h-20 mb-6 flex items-center justify-center">
                    <svg width="80" height="80" viewBox="0 0 100 100" className="overflow-visible">
                      {card.id === 1 && (
                        <>
                          {/* Discovery Call - Ultra-Dense Central Hub Style */}
                          {/* Outer Ring Nodes */}
                          <motion.circle cx="15" cy="15" r="1.2" fill="#3b82f6" opacity="0.8" 
                            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                          />
                          <motion.circle cx="85" cy="15" r="1.2" fill="#10b981" opacity="0.8"
                            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.circle cx="15" cy="85" r="1.2" fill="#f59e0b" opacity="0.8"
                            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.circle cx="85" cy="85" r="1.2" fill="#ef4444" opacity="0.8"
                            animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          
                          {/* Secondary Ring Nodes */}
                          <motion.circle cx="30" cy="30" r="1" fill="#8b5cf6" opacity="0.7"
                            animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.circle cx="70" cy="30" r="1" fill="#06b6d4" opacity="0.7"
                            animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="30" cy="70" r="1" fill="#a855f7" opacity="0.7"
                            animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="70" cy="70" r="1" fill="#22c55e" opacity="0.7"
                            animate={{ scale: [0.8, 1.3, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          {/* Micro Nodes */}
                          <motion.circle cx="10" cy="50" r="0.8" fill="#3b82f6" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.circle cx="90" cy="50" r="0.8" fill="#10b981" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="50" cy="10" r="0.8" fill="#f59e0b" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="50" cy="90" r="0.8" fill="#ef4444" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          {/* Central Hub */}
                          <motion.circle cx="50" cy="50" r="3.5" fill="#8b5cf6" opacity="0.9"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                          />
                          
                          {/* Primary Connections to Center */}
                          <motion.line x1="15" y1="15" x2="50" y2="50" stroke="#3b82f6" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                          />
                          <motion.line x1="85" y1="15" x2="50" y2="50" stroke="#10b981" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.line x1="15" y1="85" x2="50" y2="50" stroke="#f59e0b" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.line x1="85" y1="85" x2="50" y2="50" stroke="#ef4444" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          
                          {/* Secondary Connections */}
                          <motion.line x1="30" y1="30" x2="50" y2="50" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.line x1="70" y1="30" x2="50" y2="50" stroke="#06b6d4" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.line x1="30" y1="70" x2="50" y2="50" stroke="#a855f7" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.line x1="70" y1="70" x2="50" y2="50" stroke="#22c55e" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          {/* Micro Connections */}
                          <motion.line x1="10" y1="50" x2="50" y2="50" stroke="#3b82f6" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.5, 0.05] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.line x1="90" y1="50" x2="50" y2="50" stroke="#10b981" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.5, 0.05] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.line x1="50" y1="10" x2="50" y2="50" stroke="#f59e0b" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.5, 0.05] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.line x1="50" y1="90" x2="50" y2="50" stroke="#ef4444" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.5, 0.05] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          {/* Cross Connections */}
                          <motion.line x1="15" y1="15" x2="85" y2="85" stroke="#8b5cf6" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.02, 0.4, 0.02] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.line x1="85" y1="15" x2="15" y2="85" stroke="#10b981" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.02, 0.4, 0.02] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          
                          {/* Ultra-Micro Connections */}
                          <motion.line x1="30" y1="30" x2="70" y2="70" stroke="#3b82f6" strokeWidth="0.1" opacity="0.2"
                            animate={{ opacity: [0.01, 0.3, 0.01] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.line x1="70" y1="30" x2="30" y2="70" stroke="#10b981" strokeWidth="0.1" opacity="0.2"
                            animate={{ opacity: [0.01, 0.3, 0.01] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                          />
                        </>
                      )}
                      
                      {card.id === 2 && (
                        <>
                          {/* Build & Production - Ultra-Dense Network Mesh Style */}
                          {/* Outer Ring - 8 nodes */}
                          <motion.circle cx="20" cy="20" r="1.1" fill="#8b5cf6" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                          />
                          <motion.circle cx="50" cy="15" r="1.1" fill="#a855f7" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.circle cx="80" cy="20" r="1.1" fill="#c084fc" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.circle cx="85" cy="50" r="1.1" fill="#e879f9" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.circle cx="80" cy="80" r="1.1" fill="#f0abfc" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          <motion.circle cx="50" cy="85" r="1.1" fill="#f3e8ff" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                          />
                          <motion.circle cx="20" cy="80" r="1.1" fill="#8b5cf6" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                          />
                          <motion.circle cx="15" cy="50" r="1.1" fill="#a855f7" opacity="0.8"
                            animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
                          />
                          
                          {/* Middle Ring - 6 nodes */}
                          <motion.circle cx="30" cy="30" r="1" fill="#8b5cf6" opacity="0.7"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.circle cx="70" cy="30" r="1" fill="#a855f7" opacity="0.7"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="70" cy="70" r="1" fill="#c084fc" opacity="0.7"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="30" cy="70" r="1" fill="#e879f9" opacity="0.7"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          <motion.circle cx="50" cy="35" r="1" fill="#f0abfc" opacity="0.7"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
                          />
                          <motion.circle cx="50" cy="65" r="1" fill="#f3e8ff" opacity="0.7"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
                          />
                          
                          {/* Inner Ring - 4 nodes */}
                          <motion.circle cx="40" cy="40" r="1.2" fill="#8b5cf6" opacity="0.8"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.circle cx="60" cy="40" r="1.2" fill="#a855f7" opacity="0.8"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.circle cx="40" cy="60" r="1.2" fill="#c084fc" opacity="0.8"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                          />
                          <motion.circle cx="60" cy="60" r="1.2" fill="#e879f9" opacity="0.8"
                            animate={{ scale: [0.9, 1.3, 0.9], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
                          />
                          
                          {/* Micro Nodes */}
                          <motion.circle cx="35" cy="50" r="0.7" fill="#8b5cf6" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.circle cx="65" cy="50" r="0.7" fill="#a855f7" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="50" cy="25" r="0.7" fill="#c084fc" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="50" cy="75" r="0.7" fill="#e879f9" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          {/* Dense Mesh Connections - Outer Ring */}
                          <motion.line x1="20" y1="20" x2="50" y2="15" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                          />
                          <motion.line x1="50" y1="15" x2="80" y2="20" stroke="#a855f7" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.line x1="80" y1="20" x2="85" y2="50" stroke="#c084fc" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.line x1="85" y1="50" x2="80" y2="80" stroke="#e879f9" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.line x1="80" y1="80" x2="50" y2="85" stroke="#f0abfc" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          <motion.line x1="50" y1="85" x2="20" y2="80" stroke="#f3e8ff" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                          />
                          <motion.line x1="20" y1="80" x2="15" y2="50" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                          />
                          <motion.line x1="15" y1="50" x2="20" y2="20" stroke="#a855f7" strokeWidth="0.3" opacity="0.5"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
                          />
                          
                          {/* Cross Connections */}
                          <motion.line x1="20" y1="20" x2="80" y2="80" stroke="#8b5cf6" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.line x1="80" y1="20" x2="20" y2="80" stroke="#a855f7" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                          />
                          <motion.line x1="50" y1="15" x2="50" y2="85" stroke="#c084fc" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.line x1="15" y1="50" x2="85" y2="50" stroke="#e879f9" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1.8 }}
                          />
                          
                          {/* Ultra-Micro Connections */}
                          <motion.line x1="30" y1="30" x2="70" y2="70" stroke="#8b5cf6" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.02, 0.3, 0.02] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.line x1="70" y1="30" x2="30" y2="70" stroke="#a855f7" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.02, 0.3, 0.02] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.6 }}
                          />
                          <motion.line x1="40" y1="40" x2="60" y2="60" stroke="#c084fc" strokeWidth="0.1" opacity="0.2"
                            animate={{ opacity: [0.01, 0.25, 0.01] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          <motion.line x1="60" y1="40" x2="40" y2="60" stroke="#e879f9" strokeWidth="0.1" opacity="0.2"
                            animate={{ opacity: [0.01, 0.25, 0.01] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                          />
                        </>
                      )}
                      
                      {card.id === 3 && (
                        <>
                          {/* Implementation - Ultra-Dense Flow Style */}
                          {/* Top Row - 5 nodes */}
                          <motion.circle cx="10" cy="20" r="1.3" fill="#10b981" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                          />
                          <motion.circle cx="30" cy="20" r="1.3" fill="#22c55e" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.circle cx="50" cy="20" r="1.3" fill="#16a34a" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.circle cx="70" cy="20" r="1.3" fill="#059669" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.circle cx="90" cy="20" r="1.3" fill="#047857" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          
                          {/* Middle Row - 5 nodes */}
                          <motion.circle cx="10" cy="50" r="1.1" fill="#065f46" opacity="0.7"
                            animate={{ scale: [0.9, 1.4, 0.9], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.circle cx="30" cy="50" r="1.1" fill="#064e3b" opacity="0.7"
                            animate={{ scale: [0.9, 1.4, 0.9], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="50" cy="50" r="1.1" fill="#14532d" opacity="0.7"
                            animate={{ scale: [0.9, 1.4, 0.9], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="70" cy="50" r="1.1" fill="#166534" opacity="0.7"
                            animate={{ scale: [0.9, 1.4, 0.9], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          <motion.circle cx="90" cy="50" r="1.1" fill="#15803d" opacity="0.7"
                            animate={{ scale: [0.9, 1.4, 0.9], opacity: [0.4, 0.9, 0.4] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
                          />
                          
                          {/* Bottom Row - 5 nodes */}
                          <motion.circle cx="10" cy="80" r="1.3" fill="#10b981" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="30" cy="80" r="1.3" fill="#22c55e" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="50" cy="80" r="1.3" fill="#16a34a" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          <motion.circle cx="70" cy="80" r="1.3" fill="#059669" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
                          />
                          <motion.circle cx="90" cy="80" r="1.3" fill="#047857" opacity="0.8"
                            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
                          />
                          
                          {/* Micro Nodes */}
                          <motion.circle cx="20" cy="35" r="0.8" fill="#10b981" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.circle cx="40" cy="35" r="0.8" fill="#22c55e" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.circle cx="60" cy="35" r="0.8" fill="#16a34a" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.circle cx="80" cy="35" r="0.8" fill="#059669" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          <motion.circle cx="20" cy="65" r="0.8" fill="#047857" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.circle cx="40" cy="65" r="0.8" fill="#065f46" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.circle cx="60" cy="65" r="0.8" fill="#064e3b" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.circle cx="80" cy="65" r="0.8" fill="#14532d" opacity="0.6"
                            animate={{ scale: [0.6, 1.2, 0.6], opacity: [0.3, 0.8, 0.3] }}
                            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          
                          {/* Horizontal Flow Lines - Top Row */}
                          <motion.line x1="10" y1="20" x2="30" y2="20" stroke="#10b981" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                          />
                          <motion.line x1="30" y1="20" x2="50" y2="20" stroke="#22c55e" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.line x1="50" y1="20" x2="70" y2="20" stroke="#16a34a" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.line x1="70" y1="20" x2="90" y2="20" stroke="#059669" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          
                          {/* Horizontal Flow Lines - Bottom Row */}
                          <motion.line x1="10" y1="80" x2="30" y2="80" stroke="#10b981" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.line x1="30" y1="80" x2="50" y2="80" stroke="#22c55e" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.line x1="50" y1="80" x2="70" y2="80" stroke="#16a34a" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          <motion.line x1="70" y1="80" x2="90" y2="80" stroke="#059669" strokeWidth="0.4" opacity="0.7"
                            animate={{ opacity: [0.2, 0.8, 0.2] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
                          />
                          
                          {/* Vertical Flow Connections */}
                          <motion.line x1="10" y1="20" x2="10" y2="80" stroke="#10b981" strokeWidth="0.3" opacity="0.6"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.line x1="30" y1="20" x2="30" y2="80" stroke="#22c55e" strokeWidth="0.3" opacity="0.6"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.line x1="50" y1="20" x2="50" y2="80" stroke="#16a34a" strokeWidth="0.3" opacity="0.6"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.line x1="70" y1="20" x2="70" y2="80" stroke="#059669" strokeWidth="0.3" opacity="0.6"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          <motion.line x1="90" y1="20" x2="90" y2="80" stroke="#047857" strokeWidth="0.3" opacity="0.6"
                            animate={{ opacity: [0.1, 0.6, 0.1] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
                          />
                          
                          {/* Diagonal Flow Connections */}
                          <motion.line x1="10" y1="20" x2="30" y2="80" stroke="#10b981" strokeWidth="0.25" opacity="0.5"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                          />
                          <motion.line x1="30" y1="20" x2="50" y2="80" stroke="#22c55e" strokeWidth="0.25" opacity="0.5"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                          />
                          <motion.line x1="50" y1="20" x2="70" y2="80" stroke="#16a34a" strokeWidth="0.25" opacity="0.5"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                          />
                          <motion.line x1="70" y1="20" x2="90" y2="80" stroke="#059669" strokeWidth="0.25" opacity="0.5"
                            animate={{ opacity: [0.05, 0.4, 0.05] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                          />
                          
                          {/* Cross Flow Connections */}
                          <motion.line x1="20" y1="35" x2="40" y2="65" stroke="#10b981" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.02, 0.3, 0.02] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.line x1="40" y1="35" x2="60" y2="65" stroke="#22c55e" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.02, 0.3, 0.02] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.line x1="60" y1="35" x2="80" y2="65" stroke="#16a34a" strokeWidth="0.2" opacity="0.4"
                            animate={{ opacity: [0.02, 0.3, 0.02] }}
                            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                          
                          {/* Ultra-Micro Flow Lines */}
                          <motion.line x1="20" y1="35" x2="30" y2="20" stroke="#10b981" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.01, 0.25, 0.01] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                          />
                          <motion.line x1="40" y1="35" x2="50" y2="20" stroke="#22c55e" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.01, 0.25, 0.01] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                          />
                          <motion.line x1="60" y1="35" x2="70" y2="20" stroke="#16a34a" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.01, 0.25, 0.01] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          />
                          <motion.line x1="80" y1="35" x2="90" y2="20" stroke="#059669" strokeWidth="0.15" opacity="0.3"
                            animate={{ opacity: [0.01, 0.25, 0.01] }}
                            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                          />
                        </>
                      )}
                    </svg>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-4 text-center">
                    {card.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-gray-300 text-center leading-relaxed max-w-sm">
                    {card.description}
                  </p>
                  
                  {/* Step number indicator */}
                  <div className={`absolute top-4 right-4 w-8 h-8 rounded-full bg-gradient-to-br ${card.color} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                    {card.id}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Vertical Layout */}
      <div className="get-started-mobile-layout">
        <h2 className="get-started-mobile-title">Ready To Get Started?</h2>
        
        {/* Motion Graphic */}
        <div className="get-started-mobile-motion">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              transition: {
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1]
              }
            }}
          >
            <svg
              width="150"
              height="150"
              viewBox="0 0 100 100"
            >
              <motion.circle
                cx="20" cy="20" r="3"
                fill="#1f2937"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.circle
                cx="80" cy="20" r="3"
                fill="#374151"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
              <motion.circle
                cx="20" cy="80" r="3"
                fill="#4b5563"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 1.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.6
                }}
              />
              <motion.circle
                cx="80" cy="80" r="3"
                fill="#6b7280"
                animate={{ 
                  scale: [0.8, 1.2, 0.8],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{ 
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.9
                }}
              />
              <motion.circle
                cx="50" cy="50" r="4"
                fill="#111827"
                animate={{ 
                  scale: [0.9, 1.3, 0.9],
                  opacity: [0.7, 1, 0.7]
                }}
                transition={{ 
                  duration: 2.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.4
                }}
              />
              <motion.line
                x1="20" y1="20" x2="80" y2="20"
                stroke="#374151" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              />
              <motion.line
                x1="20" y1="80" x2="80" y2="80"
                stroke="#4b5563" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              />
              <motion.line
                x1="20" y1="20" x2="20" y2="80"
                stroke="#6b7280" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              />
              <motion.line
                x1="80" y1="20" x2="80" y2="80"
                stroke="#9ca3af" strokeWidth="0.5" opacity="0.4"
                animate={{ opacity: [0.2, 0.8, 0.2] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              />
              <motion.line
                x1="20" y1="20" x2="50" y2="50"
                stroke="#6b7280" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 2.1, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
              />
              <motion.line
                x1="80" y1="20" x2="50" y2="50"
                stroke="#9ca3af" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
              />
              <motion.line
                x1="20" y1="80" x2="50" y2="50"
                stroke="#374151" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
              />
              <motion.line
                x1="80" y1="80" x2="50" y2="50"
                stroke="#4b5563" strokeWidth="0.4" opacity="0.5"
                animate={{ opacity: [0.2, 0.7, 0.2] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
              />
            </svg>
          </motion.div>
        </div>
        
        {/* Cards */}
        <div className="get-started-mobile-cards">
          {cards.map((card, index) => (
            <motion.div
              key={card.id}
              className="get-started-mobile-card rounded-2xl shadow-2xl border-2 border-gray-300 overflow-hidden"
              style={{ backgroundColor: 'oklch(0.23 0.002 264)' }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 0.6, 
                ease: "easeOut", 
                delay: index * 0.2 
              }}
              viewport={{ once: true, margin: "-50px" }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${card.color} opacity-5`}></div>
              
              <div className="absolute inset-0 opacity-10">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <pattern id={`mobile-pattern-${card.id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                      <circle cx="10" cy="10" r="1" fill="currentColor" opacity="0.3"/>
                      <line x1="0" y1="10" x2="20" y2="10" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
                      <line x1="10" y1="0" x2="10" y2="20" stroke="currentColor" strokeWidth="0.5" opacity="0.2"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill={`url(#mobile-pattern-${card.id})`}/>
                </svg>
              </div>

              <div className="get-started-mobile-card-content">
                <div className="get-started-mobile-card-icon">
                  <svg width="60" height="60" viewBox="0 0 100 100" className="overflow-visible">
                    {card.id === 1 && (
                      <>
                        <motion.circle cx="15" cy="15" r="1.2" fill="#3b82f6" opacity="0.8" 
                          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                        />
                        <motion.circle cx="85" cy="15" r="1.2" fill="#10b981" opacity="0.8"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        />
                        <motion.circle cx="15" cy="85" r="1.2" fill="#f59e0b" opacity="0.8"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                        />
                        <motion.circle cx="85" cy="85" r="1.2" fill="#ef4444" opacity="0.8"
                          animate={{ scale: [1, 1.4, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                        />
                        <motion.circle cx="50" cy="50" r="3.5" fill="#8b5cf6" opacity="0.9"
                          animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        />
                        <motion.line x1="15" y1="15" x2="50" y2="50" stroke="#3b82f6" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                        />
                        <motion.line x1="85" y1="15" x2="50" y2="50" stroke="#10b981" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        />
                        <motion.line x1="15" y1="85" x2="50" y2="50" stroke="#f59e0b" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                        />
                        <motion.line x1="85" y1="85" x2="50" y2="50" stroke="#ef4444" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                        />
                      </>
                    )}
                    {card.id === 2 && (
                      <>
                        <motion.circle cx="20" cy="20" r="1.1" fill="#8b5cf6" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                        />
                        <motion.circle cx="50" cy="15" r="1.1" fill="#a855f7" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        />
                        <motion.circle cx="80" cy="20" r="1.1" fill="#c084fc" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                        />
                        <motion.circle cx="85" cy="50" r="1.1" fill="#e879f9" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                        />
                        <motion.circle cx="80" cy="80" r="1.1" fill="#f0abfc" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                        />
                        <motion.circle cx="50" cy="85" r="1.1" fill="#f3e8ff" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                        />
                        <motion.circle cx="20" cy="80" r="1.1" fill="#8b5cf6" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                        />
                        <motion.circle cx="15" cy="50" r="1.1" fill="#a855f7" opacity="0.8"
                          animate={{ scale: [0.8, 1.4, 0.8], opacity: [0.4, 0.9, 0.4] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1.4 }}
                        />
                        <motion.line x1="20" y1="20" x2="50" y2="15" stroke="#8b5cf6" strokeWidth="0.3" opacity="0.5"
                          animate={{ opacity: [0.1, 0.6, 0.1] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                        />
                        <motion.line x1="50" y1="15" x2="80" y2="20" stroke="#a855f7" strokeWidth="0.3" opacity="0.5"
                          animate={{ opacity: [0.1, 0.6, 0.1] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        />
                      </>
                    )}
                    {card.id === 3 && (
                      <>
                        <motion.circle cx="10" cy="20" r="1.3" fill="#10b981" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                        />
                        <motion.circle cx="30" cy="20" r="1.3" fill="#22c55e" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        />
                        <motion.circle cx="50" cy="20" r="1.3" fill="#16a34a" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                        />
                        <motion.circle cx="70" cy="20" r="1.3" fill="#059669" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                        />
                        <motion.circle cx="90" cy="20" r="1.3" fill="#047857" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
                        />
                        <motion.circle cx="10" cy="80" r="1.3" fill="#10b981" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                        />
                        <motion.circle cx="30" cy="80" r="1.3" fill="#22c55e" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                        />
                        <motion.circle cx="50" cy="80" r="1.3" fill="#16a34a" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.7 }}
                        />
                        <motion.circle cx="70" cy="80" r="1.3" fill="#059669" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.9 }}
                        />
                        <motion.circle cx="90" cy="80" r="1.3" fill="#047857" opacity="0.8"
                          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 1.1 }}
                        />
                        <motion.line x1="10" y1="20" x2="30" y2="20" stroke="#10b981" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0 }}
                        />
                        <motion.line x1="30" y1="20" x2="50" y2="20" stroke="#22c55e" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                        />
                        <motion.line x1="50" y1="20" x2="70" y2="20" stroke="#16a34a" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                        />
                        <motion.line x1="70" y1="20" x2="90" y2="20" stroke="#059669" strokeWidth="0.4" opacity="0.7"
                          animate={{ opacity: [0.2, 0.8, 0.2] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                        />
                      </>
                    )}
                  </svg>
                </div>
                
                <h3 className="get-started-mobile-card-title">{card.title}</h3>
                <p className="get-started-mobile-card-description">{card.description}</p>
                
                <div className={`get-started-mobile-step-number bg-gradient-to-br ${card.color}`}>
                  {card.id}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GetStartedSection;
